<template>
  <Teleport to="body">
    <div class="split-times-backdrop" @click.self="emit('close')">
      <div class="split-times" role="dialog" aria-modal="true" :aria-label="$t('recordings.activity.split_times')">
        <header class="split-times__header">
          <h3 class="split-times__title">{{ $t('recordings.activity.split_times') }}</h3>
          <button type="button" class="split-times__close" :aria-label="$t('recordings.activity.close')" @click="emit('close')">
            <span class="material-icons" aria-hidden="true">close</span>
          </button>
        </header>

        <div v-if="splits" class="split-times__body">
          <div class="split-times__figures">
            <div v-for="figure in figures" :key="figure.label">
              <small class="text-muted d-block mb-1">{{ figure.label }}</small>
              <span class="split-times__figure" :class="{ 'split-times__figure--fastest': figure.isFastest }">
                <template v-if="figure.leading"><strong>{{ figure.leading.value }}</strong> <small>{{ figure.leading.unit }}</small>{{ ' ' }}</template>
                <strong>{{ figure.value }}</strong> <small>{{ figure.unit }}</small>
              </span>
            </div>
          </div>

          <SplitStepper :state="state" :unit="pref.kilometerSymbol()" :count="splits.splits.length" />

          <PaceSplitBars :splits="splits" :pref="pref" :backdrop="activity.elevation?.samples" :selected="selected" @select="state.select" />

          <div class="split-times__picked">
            <strong>{{ picked.title }}</strong>
            <span v-if="badge" class="split-times__badge" :class="`split-times__badge--${badge}`">{{ badge === 'fastest' ? $t('recordings.activity.fastest') : $t('recordings.activity.slowest') }}</span>
            <span class="split-times__picked-figures">
              <strong>{{ picked.pace }}</strong>
              <span v-for="line in picked.lines" :key="line" class="text-muted">{{ line }}</span>
            </span>
          </div>

          <div class="split-times__table-scroll">
            <table class="split-times__table">
              <thead>
                <tr>
                  <th v-for="column in columns" :key="column.label" :class="{ 'text-start': column.key === 'distance' }">
                    <button type="button" class="split-times__sort" @click="sortBy(column.key)">
                      {{ column.label }}
                      <span v-if="column.isMarked" class="material-icons" aria-hidden="true">
                        {{ isDescending ? 'expand_more' : 'expand_less' }}
                      </span>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in rows"
                  :key="row.index"
                  class="split-times__row"
                  :class="{ 'split-times__row--selected': row.index === selected }"
                  @click="state.select(row.index)"
                >
                  <td class="text-start"><strong>{{ row.distance }}</strong></td>
                  <td>{{ row.time }}</td>
                  <td :class="{ 'split-times__fastest': row.index === splits.fastestIndex }"><strong>{{ row.pace }}</strong></td>
                  <td :class="`split-times__difference--${row.trend}`">{{ row.difference }}</td>
                  <td class="text-muted">{{ row.elevation }}</td>
                  <td v-if="state.hasHeartRate" class="text-muted">{{ row.heartRate }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';
import { paceUnit, paceValue } from '../../../utils/recordingActivity/format';
import {
  elevationChangeLabel,
  paceDifferenceLabel,
  paceDifferenceSeconds,
  sortedSplitIndices,
  splitDetail,
  splitDurationLabel,
  splitEndLabel,
  type SplitColumn,
} from '../../../utils/recordingActivity/paceSplits';
import { durationFigure, type ActivityFigure, type RecordingActivity } from '../../../utils/recordingActivity/recordingActivity';
import type { PaceSplitsState } from '../usePaceSplits';
import PaceSplitBars from './PaceSplitBars.vue';
import SplitStepper from './SplitStepper.vue';

const props = defineProps<{ state: PaceSplitsState; activity: RecordingActivity; pref: MeasurementPrefUtility }>();
const emit = defineEmits<{ (e: 'close'): void }>();

const { t } = useI18n();
const sortColumn = ref<SplitColumn>('distance');
const isDescending = ref(false);
const splits = computed(() => props.state.splits.value);
const selected = computed(() => props.state.selected.value);
// The splits are timed stops and all, so they are measured against the pace that includes them.
const average = props.activity.pace!.overallSecondsPerMeter;

const figures = computed<Array<ActivityFigure & { isFastest?: boolean }>>(() => {
  const fastest = splits.value!.splits[splits.value!.fastestIndex].secondsPerMeter!;
  const unit = paceUnit(props.pref);
  const { movingSeconds, stoppedSeconds } = props.activity.pace!;
  return [
    { label: t('recordings.activity.avg_pace'), value: paceValue(average, props.pref), unit },
    { label: t('recordings.activity.fastest_split'), value: paceValue(fastest, props.pref), unit, isFastest: true },
    durationFigure(t('recordings.activity.total_time'), movingSeconds + stoppedSeconds, t),
    durationFigure(t('recordings.activity.moving_time'), movingSeconds, t),
    durationFigure(t('recordings.activity.stopped_time'), stoppedSeconds, t),
  ];
});

const picked = computed(() => splitDetail(splits.value!, selected.value, props.pref, t));
const badge = computed(() => {
  if (selected.value === splits.value!.fastestIndex) return 'fastest';
  if (selected.value === splits.value!.slowestIndex) return 'slowest';
  return null;
});

const columns = computed(() => {
  const all: Array<{ key: SplitColumn; label: string; isMarked: boolean }> = [
    { key: 'distance', label: capitalized(props.pref.kilometerSymbol()), isMarked: sortColumn.value === 'distance' },
    { key: 'time', label: t('recordings.activity.time'), isMarked: sortColumn.value === 'time' },
    { key: 'pace', label: t('recordings.activity.pace'), isMarked: sortColumn.value === 'pace' },
    { key: 'pace', label: t('recordings.activity.vs_avg'), isMarked: false },
    { key: 'elevation', label: t('recordings.activity.elev_unit', { unit: props.pref.meterSymbol() }), isMarked: sortColumn.value === 'elevation' },
  ];
  if (props.state.hasHeartRate) {
    all.push({ key: 'heartRate', label: t('recordings.activity.hr'), isMarked: sortColumn.value === 'heartRate' });
  }
  return all;
});

const rows = computed(() =>
  sortedSplitIndices(splits.value!, sortColumn.value, isDescending.value).map((index) => {
    const split = splits.value!.splits[index];
    const pace = split.secondsPerMeter;
    const difference = pace === null ? null : paceDifferenceSeconds(pace, average, props.pref);
    return {
      index,
      distance: splitEndLabel(split, props.pref),
      time: pace === null ? '–' : splitDurationLabel(split.movingSeconds),
      pace: pace === null ? '–' : paceValue(pace, props.pref),
      difference: difference === null ? '–' : paceDifferenceLabel(difference),
      trend: difference === null || difference === 0 ? 'level' : difference < 0 ? 'faster' : 'slower',
      elevation: split.elevationChangeMeters === null ? '–' : elevationChangeLabel(split.elevationChangeMeters, props.pref, false),
      heartRate: split.averageBpm === null ? '–' : String(split.averageBpm),
    };
  })
);

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function sortBy(column: SplitColumn) {
  if (column === sortColumn.value) {
    isDescending.value = !isDescending.value;
  } else {
    sortColumn.value = column;
    isDescending.value = false;
  }
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') emit('close');
}

let bodyOverflow = '';
onMounted(() => {
  window.addEventListener('keydown', onKeydown);
  bodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  document.body.style.overflow = bodyOverflow;
});
</script>

<style scoped>
.split-times-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.6);
}

.split-times {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 900px;
  max-height: 100%;
  color: #fff;
  background: var(--gray-base, #17181d);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
}

@media (max-width: 575.98px) {
  .split-times-backdrop {
    padding: 0;
  }

  .split-times {
    height: 100%;
    border-radius: 0;
  }
}

.split-times__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.split-times__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.split-times__close {
  display: inline-flex;
  padding: 0.25rem;
  color: #fff;
  background: transparent;
  border: 0;
}

.split-times__body {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  overflow-y: auto;
}

.split-times__figures {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 3rem;
}

.split-times__figure strong {
  font-size: 1.5rem;
}

.split-times__figure small {
  color: var(--text-muted, #b2b2b2);
}

.split-times__figure--fastest strong {
  color: #ffdf46;
}

.split-times__picked {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

.split-times__picked-figures {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-left: auto;
}

.split-times__badge {
  padding: 0.1rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 999px;
}

.split-times__badge--fastest {
  color: #17181d;
  background: #ffdf46;
}

.split-times__badge--slowest {
  color: #fff;
  background: var(--gray-hover, #30323b);
}

.split-times__table-scroll {
  flex-shrink: 0;
  overflow-x: auto;
}

.split-times__table {
  width: 100%;
  font-size: 0.9rem;
  border-collapse: separate;
  border-spacing: 0;
}

.split-times__table th,
.split-times__table td {
  padding: 0.6rem 0.75rem;
  text-align: right;
  white-space: nowrap;
}

.split-times__table th {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.split-times__sort {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0;
  font-weight: 400;
  color: var(--text-muted, #b2b2b2);
  background: transparent;
  border: 0;
}

.split-times__sort .material-icons {
  font-size: 18px;
}

.split-times__row {
  cursor: pointer;
}

.split-times__row:hover td {
  background: rgba(255, 255, 255, 0.04);
}

.split-times__row--selected td,
.split-times__row--selected:hover td {
  background: var(--gray-raised, #2a2c34);
}

.split-times__row td:first-child {
  border-radius: 6px 0 0 6px;
}

.split-times__row td:last-child {
  border-radius: 0 6px 6px 0;
}

.split-times__fastest strong {
  color: #ffdf46;
}

.split-times__difference--faster {
  color: #4bc263;
}

.split-times__difference--slower {
  color: #f0883e;
}

.split-times__difference--level {
  color: var(--text-muted, #b2b2b2);
}
</style>
